import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AIPercentileChart = ({ title, chartData }) => {
  // Validate input data
  const validChartData = Array.isArray(chartData) && chartData.length > 0 ? chartData : [];
  
  // Check if there's actually data to display
  const hasData = validChartData.length > 0 && validChartData.some(series => 
    Array.isArray(series.data) && series.data.length > 0
  );
  
  // If no data, show empty state
  if (!hasData) {
    return (
      <div className="ai-chart-wrapper">
        <h4 className="ai-chart-title">{title}</h4>
        <div className="ai-chart-empty">
          <p>No data available</p>
        </div>
      </div>
    );
  }

  // Default percentile colors (gradient from cool to warm)
  const percentileColors = {
    'p10': 'rgba(54, 162, 235, 0.8)',   // Blue
    'p25': 'rgba(75, 192, 192, 0.8)',   // Teal
    'p50': 'rgba(255, 205, 86, 0.8)',   // Yellow
    'p75': 'rgba(255, 159, 64, 0.8)',   // Orange
    'p90': 'rgba(255, 99, 132, 0.8)',   // Red
  };

  // Extract labels and data
  const labels = validChartData.map(series => series.name);
  const dataValues = validChartData.map(series => series.data[0] || 0);
  
  // Assign colors based on percentile name or use provided color
  const colors = validChartData.map((series) => {
    if (series.color) return series.color;
    
    // Try to match percentile pattern (p10, p25, p50, p75, p90)
    const percentileMatch = series.name.toLowerCase().match(/p(\d+)/);
    if (percentileMatch) {
      const key = `p${percentileMatch[1]}`;
      return percentileColors[key] || 'rgba(153, 102, 255, 0.8)';
    }
    
    return 'rgba(153, 102, 255, 0.8)'; // Default purple
  });

  const data = {
    labels: labels,
    datasets: [{
      label: 'Value',
      data: dataValues,
      backgroundColor: colors,
      borderColor: colors.map(color => color.replace('0.8', '1')),
      borderWidth: 1,
    }]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 13,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        },
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed.y;
            return `${label}: ${value.toFixed(2)}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 12,
            weight: 'bold'
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11
          }
        },
        title: {
          display: true,
          text: 'Value',
          font: {
            size: 12,
            weight: 'bold'
          }
        }
      }
    }
  };

  return (
    <div className="ai-chart-wrapper">
      <h4 className="ai-chart-title">{title}</h4>
      <div className="ai-chart-container">
        <Bar data={data} options={options} />
      </div>
      <div className="ai-percentile-legend">
        <span className="ai-percentile-note">
          Percentile distribution across the fleet
        </span>
      </div>
    </div>
  );
};

export default AIPercentileChart;
